import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Steps, Button, Icon, Spin, Tooltip, message, Modal } from 'antd'
import './styles.scss'
import NewHeader from '../../components/NewHeader'
import UploadDocument from './components/UploadDocument'
import Voiceover from './components/Voiceover'
import ShotList from './components/ShotList'
import ImageGeneration from './components/ImageGeneration'
import VideoGeneration from './components/VideoGeneration'
import VideoStyle from './components/VideoStyle'
import { useImageGeneration } from './components/ImageGeneration/hooks/useImageGeneration'
import { useVideoGeneration } from './components/VideoGeneration/hooks/useVideoGeneration'
import { useCharacters } from './hooks/useCharacters'

import {
  changeMachineState,
  createAiVideo,
  generateShots,
  getAiVideoById,
  groupShotsToScenes,
  updateAiVideo,
  getTaskStatus,
  clusterScenesWithAudio,
  generateLocationReference
} from '../../services/api/aiVideoGeneration'
import history from '../../history'
import _ from 'lodash'
import { a } from 'aws-amplify'

const { Step } = Steps

const MAX_RETRIES = 10
const POLL_INTERVAL = 15000

const defaultValidation = {
  uploadDocument: false,
  voiceover: false,
  videoStyle: false,
  shotList: false,
  imageGeneration: false,
  videoGeneration: false
}

const defaultData = {
  file: null,
  voiceoverText: '',
  voiceoverAudio: null,
  videoStyle: {},
  aspectRatio: '16:9',
  selectedCharacter: null,
  characterList: [],
  characterMap: null,
  shotList: [],
  shotLoading: false,
  locationMap: null,
  locationReference: null,
  selectedVoice: null
}

const VideoAi = props => {
  const [currentStep, setCurrentStep] = useState(0)
  const initialStepHandled = useRef(false)
  // Create a ref to access the VideoGeneration component
  const videoGenerationRef = useRef(null)
  const imageGenerationRef = useRef(null)
  const isFetchingShots = useRef(false)

  const [isFetchingVideoData, setIsFetchingVideoData] = useState(false)
  const [isCloning, setIsCloning] = useState(false)

  const [showProcessShots, setShowProcessShots] = useState(false)
  const [isFineTunedLoading, setIsFineTunedLoading] = useState(false)
  const [isSavingVideoData, setIsSavingVideoData] = useState(false)
  const [
    isGeneratingLocationReference,
    setIsGeneratingLocationReference
  ] = useState(false)
  const [isClusteringAudio, setIsClusteringAudio] = useState(false)

  const [stepsValidation, setStepsValidation] = useState(defaultValidation)
  const [shotsGenerationProgress, setShotsGenerationProgress] = useState(0)
  const [locationGenerationProgress, setLocationGenerationProgress] = useState(
    0
  )
  const [locationGenerationStatus, setLocationGenerationStatus] = useState('')
  const [audioClusteringProgress, setAudioClusteringProgress] = useState(0)
  const [audioClusteringStatus, setAudioClusteringStatus] = useState('')

  const [formData, setFormData] = useState(defaultData)

  const { videoId, initialStep, handleGoBack } = props

  useEffect(() => {
    if (videoId) {
      // Reset form data to default state when switching video IDs to prevent stale data
      setFormData(defaultData)
      fetchVideoData()
    }
  }, [videoId])

  const [imageProvider, setImageProvider] = useState('DeepAI')
  const [isImageGenerationLoading, setIsImageGenerationLoading] = useState(
    false
  )

  const setScenes = action => {
    setFormData(prev => {
      const currentScenes = prev.shotList || []
      const newScenes =
        typeof action === 'function' ? action(currentScenes) : action
      return { ...prev, shotList: newScenes }
    })
  }

  const {
    generateAllImages,
    handleRegenerateImage,
    handleSelectReferenceAndRegenerate,
    retryFailedImages
  } = useImageGeneration({
    scenes: formData.shotList || [],
    setScenes,
    formData,
    imageProvider,
    setIsLoading: setIsImageGenerationLoading
  })

  const [videoProvider, setVideoProvider] = useState('luma')

  const {
    videoGenerating,
    generatingVideoForAllScenes,
    handleGenerateFullVideo: generateAllVideos,
    handleRegenerateVideo,
    handleRetryFailedVideos,
    updateShotDataRef
  } = useVideoGeneration({
    scenes: formData.shotList || [],
    setScenes,
    formData,
    videoProvider
  })

  const {
    characters,
    setCharacters,
    isFetchingCharacters,
    fetchCharacters
  } = useCharacters({
    onCharactersLoaded: chars => {
      setFormData(prev => ({ ...prev, characterList: chars }))
    }
  })

  useEffect(() => {
    const shouldFetchCharacters =
      !formData.characterList || formData.characterList.length === 0

    if (shouldFetchCharacters) {
      fetchCharacters()
    }
  }, [formData.characterList])

  const [voices, setVoices] = useState([])

  const steps = [
    {
      key: 'uploadDocument',
      title: 'Upload',
      icon: 'file-text',
      component: UploadDocument
    },
    {
      key: 'voiceover',
      title: 'Voiceover',
      icon: 'sound',
      component: Voiceover
    },
    {
      key: 'videoStyle',
      title: 'Visual Setup',
      icon: 'camera',
      component: VideoStyle
    },
    {
      key: 'shotList',
      title: 'Shot List',
      icon: 'ordered-list',
      component: ShotList,
      props: {
        locationGenerationStatus,
        locationGenerationProgress
      }
    },
    {
      key: 'imageGeneration',
      title: 'Images',
      icon: 'picture',
      component: ImageGeneration
    },
    {
      key: 'videoGeneration',
      title: 'Video',
      icon: 'video-camera',
      component: VideoGeneration,
      props: {
        videoGenerating,
        generatingVideoForAllScenes,
        handleRegenerateVideo,
        handleRetryFailedVideos,
        handleGenerateFullVideo: generateAllVideos,
        updateShotDataRef,
        videoProvider,
        setScenes
      }
    }
  ]

  // Helper to get step index from step key
  const getStepIndexByKey = stepKey => {
    if (!stepKey) return 0
    const index = steps.findIndex(s => s.key === stepKey)
    return index !== -1 ? index : 0
  }

  const updateUrlWithStep = (stepIndex, id = videoId) => {
    if (!id) return
    const stepKey = steps[stepIndex]?.key || 'uploadDocument'
    window.history.replaceState(null, '', `/video-create/${id}/${stepKey}`)
  }

  useEffect(() => {
    if (
      !isFetchingVideoData &&
      videoId &&
      initialStep &&
      !initialStepHandled.current
    ) {
      const isValidationLoaded = !_.isEqual(stepsValidation, defaultValidation)

      if (isValidationLoaded) {
        const stepIndex = getStepIndexByKey(initialStep)
        let targetStep = stepIndex
        for (let i = 0; i < stepIndex; i++) {
          if (!stepsValidation[steps[i]?.key]) {
            targetStep = i
            break
          }
        }
        setCurrentStep(targetStep)
        updateUrlWithStep(targetStep)
        initialStepHandled.current = true
      }
    }
  }, [isFetchingVideoData, videoId, initialStep, stepsValidation])

  useEffect(() => {
    handleSaveVideoData()
  }, [formData])

  const handleSaveVideoData = () => {
    if (isSavingVideoData) {
      _.debounce(handleSaveVideoData, 1000)
      return
    }

    if (videoId) {
      handleUpdateVideoData(formData)
    } else {
      handleCreateNew(formData)
    }
  }

  const handleCreateNew = async data => {
    if (_.isEqual(data, defaultData)) {
      return
    }

    try {
      setIsSavingVideoData(true)
      const payload = getPayload(data)
      const response = await createAiVideo(payload)
      const newVideoId = response && response.data && response.data.id
      const stepKey = steps[currentStep]?.key || 'uploadDocument'
      history.push(`/video-create/${newVideoId}/${stepKey}`)
    } catch (error) {
      console.error('Error creating new video:', error)
    } finally {
      setIsSavingVideoData(false)
    }
  }

  const handleUpdateVideoData = async data => {
    if (_.isEqual(data, defaultData)) {
      return
    }

    try {
      setIsSavingVideoData(true)
      const payload = getPayload(data)
      const response = await updateAiVideo(videoId, payload)

      console.log('Response:', response)
    } catch (error) {
      console.error('Error updating video data:', error)
    } finally {
      setIsSavingVideoData(false)
    }
  }

  const getPayload = data => {
    const location_map = JSON.stringify(data.locationMap)
    const location_reference = JSON.stringify(data.locationReference)
    const character_map = JSON.stringify(data.characterMap)

    const payload = {
      voiceoverText: data.voiceoverText,
      voiceoverAudio: data.voiceoverAudio,
      videoStyle: data.videoStyle,
      aspectRatio: data.aspectRatio || '16:9',
      selectedCharacter: data.selectedCharacter,
      shotList: data.shotList,
      video_url: data.finalVideo || '',
      location_map,
      location_reference,
      character_map,
      selected_voice: data.selectedVoice,
      file: null
    }
    return payload
  }

  const parseJSONSafely = (val, maxDepth = 5) => {
    if (val === null || val === undefined) return null
    let result = val
    let depth = 0

    while (typeof result === 'string' && depth < maxDepth) {
      try {
        result = JSON.parse(result)
        depth++
      } catch {
        break
      }
    }
    return result
  }

  const fetchVideoData = async () => {
    if (isFetchingVideoData) {
      return
    }

    try {
      setIsFetchingVideoData(true)
      console.log('Fetching video data for videoId:', videoId)
      const response = await getAiVideoById(videoId)
      const data = response && response.data
      const locationMap = parseJSONSafely(data && data.location_map)
      const locationReference = parseJSONSafely(data && data.location_reference)
      const characterMap = parseJSONSafely(data && data.character_map)
      const selectedCharacter =
        parseJSONSafely(data && data.selected_character) || []

      setFormData(prev => ({
        ...prev,
        shotList: (data.shot_list || []).map(scene => ({
          ...scene,
          shots: (scene.shots || []).map(shot => ({
            ...shot,
            generated_video_duration:
              shot.generated_video_duration ||
              (shot.vidSrc ? shot.video_duration : null)
          }))
        })),
        voiceoverText: data.voiceover_text,
        selectedCharacter: data.selected_character,
        videoStyle: data.video_style,
        aspectRatio: data.aspect_ratio || '16:9',
        shotsApiRes: flatShots(data.shot_list),
        finalVideo: data.video_url || '',
        locationMap,
        locationReference,
        characterMap,
        selectedCharacter,
        selectedVoice: data.selected_voice
      }))
      setStepsValidation({
        uploadDocument: Boolean(data.voiceover_text),
        voiceover: true,
        videoStyle: Boolean(data.video_style),
        shotList:
          data.shot_list &&
          data.shot_list.length > 0 &&
          (() => {
            const charKeys = characterMap ? Object.keys(characterMap) : []
            const locKeys = locationMap ? Object.keys(locationMap) : []

            const allCharsMapped = charKeys.every(key =>
              selectedCharacter.some(c => c.original === key || c.last === key)
            )

            const allLocsGenerated = locKeys.every(
              key =>
                locationReference &&
                locationReference[key] &&
                locationReference[key].uri
            )

            return allCharsMapped && allLocsGenerated
          })(),
        imageGeneration:
          data.shot_list &&
          data.shot_list.length > 0 &&
          data.shot_list.every(
            scene =>
              scene.shots.length > 0 && scene.shots.every(shot => shot.imageSrc)
          ),
        videoGeneration:
          data.shot_list &&
          data.shot_list.length > 0 &&
          data.shot_list.every(scene => scene.shots.length > 0) &&
          data.shot_list.every(scene => scene.shots.every(shot => shot.vidSrc))
      })

      const isLocationReferenceEmpty =
        !locationReference ||
        (typeof locationReference === 'object' &&
          Object.keys(locationReference).length === 0)

      if (
        isLocationReferenceEmpty &&
        locationMap &&
        Object.keys(locationMap).length > 0
      ) {
        generateLocationMap(locationMap, locationReference || {})
      }
    } catch (error) {
      console.error('Error fetching video data:', error)
    } finally {
      setIsFetchingVideoData(false)
    }
  }

  const flatShots = input =>
    input.reduce((acc, scene) => {
      scene.shots.forEach(shot => {
        // Use key if available, otherwise fall back to id
        const keyToUse = shot.key || shot.id

        // Replace spaces with hyphens
        const formattedKey = keyToUse.replace(/\s+/g, '-')
        acc[formattedKey] = shot
      })
      return acc
    }, {})

  const handleNext = () => {
    const nextStep = Math.min(currentStep + 1, steps.length - 1)
    setCurrentStep(nextStep)
    updateUrlWithStep(nextStep)
  }

  const handlePrevious = () => {
    const prevStep = Math.max(currentStep - 1, 0)
    setCurrentStep(prevStep)
    updateUrlWithStep(prevStep)
  }

  const handleOpenVideoEditor = () => {
    // Call the generate function from the VideoGeneration component using the ref
    if (
      videoGenerationRef.current &&
      videoGenerationRef.current.openVideoEditor
    ) {
      videoGenerationRef.current.openVideoEditor()
    } else {
      console.error('Video generation function not available')
    }
  }

  const handleProcessShots = () => {
    // Call the processShots function from the ImageGeneration component using the ref
    if (imageGenerationRef.current && imageGenerationRef.current.processShots) {
      imageGenerationRef.current.processShots()
    } else {
      console.error('Image generation function not available')
    }
  }

  // Handle video style change with Clone/Override options
  const handleVideoStyleChange = async (newStyle, shouldClone) => {
    if (shouldClone) {
      // Clone: Create new video instance
      try {
        setIsCloning(true)
        const payload = getPayload(formData)
        payload.videoStyle = newStyle

        // Clear generated assets for the new clone
        if (payload.shotList && payload.shotList.length > 0) {
          payload.shotList = payload.shotList.map(scene => ({
            ...scene,
            shots: scene.shots.map(shot => ({
              ...shot,
              imageSrc: null,
              vidSrc: null,
              video_duration: null,
              generated_video_duration: null
            }))
          }))
        }

        payload.video_url = null

        const response = await createAiVideo(payload)
        const newVideoId = response && response.data && response.data.id

        if (newVideoId) {
          await updateAiVideo(newVideoId, payload)
        }

        const stepKey =
          (steps[currentStep] && steps[currentStep].key) || 'videoStyle'

        Modal.success({
          title: 'Video Cloned Successfully',
          content:
            'You are now editing a new copy of the video. The original video remains unchanged.',
          onOk: () => {
            history.push(`/video-create/${newVideoId}/${stepKey}`)
          },
          centered: true
        })
      } catch (error) {
        console.error('Error cloning video:', error)
        message.error('Failed to clone video')
      } finally {
        setIsCloning(false)
      }
    } else {
      // Override: Update current video and regenerate images
      const hasGeneratedContent =
        formData.shotList &&
        formData.shotList.length > 0 &&
        formData.shotList.some(scene => scene.shots.some(shot => shot.imageSrc))

      setFormData(prev => ({
        ...prev,
        videoStyle: newStyle,
        // Clear all images to trigger full regeneration if content exists
        ...(hasGeneratedContent && {
          shotList: prev.shotList.map(scene => ({
            ...scene,
            shots: scene.shots.map(shot => ({
              ...shot,
              imageSrc: null,
              vidSrc: null // Also clear videos since images changed
            }))
          }))
        })
      }))

      if (hasGeneratedContent) {
        message.info('Images will be regenerated with new style')
      }
    }
  }

  useEffect(() => {
    if (
      formData.voiceoverText &&
      formData.shotList.length === 0 &&
      !formData.shotLoading
    ) {
      fetchShots()
    }
  }, [formData.voiceoverText, formData.shotList, formData.shotLoading])

  const fetchShots = async (retryCount = 0) => {
    if (retryCount === 0) {
      if (isFetchingShots.current) return
      isFetchingShots.current = true
    }

    setFormData(prev => ({ ...prev, shotLoading: true }))
    const storyText = formData && formData.voiceoverText

    try {
      const res = await generateShots({ story: storyText })

      console.log('Shots response:', res)
      const taskId = res && res.data && res.data.task_id
      if (taskId) {
        await pollShotsTaskStatus(taskId)
      } else {
        const shotsData = res && res.data
        await handleScenesMapping(shotsData)
      }
    } catch (err) {
      if (retryCount < MAX_RETRIES) {
        // Retry after a short delay
        setTimeout(() => fetchShots(retryCount + 1), POLL_INTERVAL)
      } else {
        setFormData(prev => ({ ...prev, shotLoading: false }))
      }
    } finally {
      if (retryCount === 0) isFetchingShots.current = false
    }
  }

  const pollShotsTaskStatus = async taskId => {
    while (true) {
      try {
        if (!isFetchingShots.current) break // Safety exit if task cancelled

        const response = await getTaskStatus(taskId)

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.output
        ) {
          const shotsData = response.data.output
          await handleScenesMapping(shotsData)
          break // Exit loop on success
        } else if (response.data && response.data.status === 'failed') {
          console.error('Shots generation task failed:', response.data)
          throw new Error('Shots generation failed')
        } else {
          setShotsGenerationProgress(
            response.data && response.data.progress
              ? response.data.progress
              : shotsGenerationProgress
          )
          // Wait before next poll
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      } catch (error) {
        console.error('Error polling shots status:', error)
        // Wait before next poll on error
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      }
    }
  }

  const pollLocationTaskStatus = async taskId => {
    while (true) {
      try {
        const response = await getTaskStatus(taskId)

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.output
        ) {
          setFormData(prev => ({
            ...prev,
            locationReference: response.data.output
          }))
          setIsGeneratingLocationReference(false)
          break
        } else if (response.data && response.data.status === 'failed') {
          console.error('Location reference task failed:', response.data)
          setIsGeneratingLocationReference(false)
          break
        } else {
          setLocationGenerationProgress(
            response.data && response.data.progress
              ? response.data.progress
              : locationGenerationProgress
          )
          setLocationGenerationStatus(
            response.data && response.data.status ? response.data.status : ''
          )
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      } catch (error) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      }
    }
  }

  const handleScenesMapping = async (data, isFineTuned) => {
    try {
      const payload = {
        input_data: data
      }

      const response = await groupShotsToScenes(payload)

      // Check if response contains task_id (async processing)
      if (response.data && response.data.task_id) {
        await pollTaskStatus(response.data.task_id, data)
      } else {
        // Direct response with output
        const sceneData = response.data && response.data.output
        const formattedSceneData = formatSceneObj(sceneData)

        setFormData(prev => ({
          ...prev,
          shotList: formattedSceneData,
          shotsApiRes: data,
          locationMap: parseJSONSafely(data.location_map),
          characterMap: parseJSONSafely(data.character_map)
        }))
        await initiateAudioClustering(sceneData, data)
      }
    } catch (error) {
      console.error('Scene mapping failed:', error)
    } finally {
      setFormData(prev => ({ ...prev, shotLoading: false }))
    }
  }

  const pollTaskStatus = async (taskId, originalData) => {
    while (true) {
      try {
        const response = await getTaskStatus(taskId)

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.output
        ) {
          // Task completed successfully
          const sceneData = response.data.output
          const formattedSceneData = formatSceneObj(sceneData)

          setFormData(prev => ({
            ...prev,
            shotList: formattedSceneData,
            shotsApiRes: originalData,
            locationMap: parseJSONSafely(originalData.location_map),
            characterMap: parseJSONSafely(originalData.character_map)
          }))

          if (originalData.location_map) {
            generateLocationMap(originalData.location_map)
          }
          await initiateAudioClustering(sceneData, originalData)
          break
        } else if (response.data && response.data.status === 'failed') {
          // Task failed
          console.error('Task failed:', response.data)
          throw new Error('Task processing failed')
        } else {
          // Task still processing, retry after delay
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      } catch (error) {
        // Retry on error after delay
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      }
    }
  }

  const initiateAudioClustering = async (sceneStructure, originalData) => {
    setIsClusteringAudio(true)
    try {
      // Build payload with scene_structure and optional voice_id
      const payload = {
        scene_structure: sceneStructure
      }

      // Include voice_id if a voice is selected
      if (formData.selectedVoice) {
        payload.voice_id = formData.selectedVoice
      }

      const response = await clusterScenesWithAudio(payload)

      const taskId = response && response.data && response.data.task_id
      if (taskId) {
        await pollAudioClustering(taskId)
      } else if (response.data && response.data.output) {
        // Direct output
        const sceneData = response.data.output
        const formattedSceneData = formatSceneObj(sceneData)
        setFormData(prev => ({
          ...prev,
          shotList: formattedSceneData
        }))
        setIsClusteringAudio(false)
      } else {
        // Handle cases where response might be unexpected or just empty
        setIsClusteringAudio(false)
      }
    } catch (error) {
      console.error('Audio clustering failed:', error)
      setIsClusteringAudio(false)
    }
  }

  const pollAudioClustering = async taskId => {
    while (true) {
      try {
        const response = await getTaskStatus(taskId)

        console.log('pollAudioClustering', response)
        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.output
        ) {
          const sceneData = response.data.output
          const formattedSceneData = formatSceneObj(sceneData)
          setFormData(prev => ({
            ...prev,
            shotList: formattedSceneData
          }))
          setIsClusteringAudio(false)
          break
        } else if (response.data && response.data.status === 'failed') {
          console.error('Audio clustering task failed:', response.data)
          setIsClusteringAudio(false)
          setAudioClusteringStatus('failed')
          break
        } else {
          setAudioClusteringProgress(
            response.data && response.data.progress ? response.data.progress : 0
          )
          setAudioClusteringStatus(
            response.data && response.data.status ? response.data.status : ''
          )
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      } catch (error) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      }
    }
  }

  const generateLocationMap = async (locationMapObj, existingUris) => {
    setIsGeneratingLocationReference(true)

    const locationRes = await generateLocationReference({
      location_map_with_prompts: locationMapObj,
      existing_location_uris:
        existingUris !== undefined ? existingUris : formData.locationReference
    })
    const taskId = locationRes && locationRes.data && locationRes.data.task_id
    if (taskId) {
      await pollLocationTaskStatus(taskId)
    } else {
      setIsGeneratingLocationReference(false)
    }
  }

  const handleAddLocationReference = async newLocationMap => {
    setIsGeneratingLocationReference(true)

    try {
      const locationRes = await generateLocationReference({
        location_map_with_prompts: {
          ...(formData.locationMap || {}),
          ...newLocationMap
        },
        existing_location_uris: formData.locationReference
      })
      const taskId = locationRes && locationRes.data && locationRes.data.task_id
      if (taskId) {
        await pollAddLocationTaskStatus(taskId, newLocationMap)
      } else {
        setIsGeneratingLocationReference(false)
      }
    } catch (error) {
      console.error('Error adding location reference:', error)
      setIsGeneratingLocationReference(false)
    }
  }

  const handleEditLocationReference = async editingLocationPrompt => {
    setIsGeneratingLocationReference(true)

    try {
      // keys being edited (e.g. ["__location1.1__"])
      const editingKeys = Object.keys(editingLocationPrompt)

      // remove old URIs for edited locations
      const filteredLocationUris = Object.keys(
        formData.locationReference || {}
      ).reduce((acc, key) => {
        if (!editingKeys.includes(key)) {
          acc[key] = formData.locationReference[key]
        }
        return acc
      }, {})

      const locationRes = await generateLocationReference({
        location_map_with_prompts: {
          ...(formData.locationMap || {}),
          ...editingLocationPrompt
        },
        existing_location_uris: filteredLocationUris
      })

      const taskId = locationRes?.data?.task_id

      if (taskId) {
        await pollAddLocationTaskStatus(taskId, editingLocationPrompt)
      } else {
        setIsGeneratingLocationReference(false)
      }
    } catch (error) {
      console.error('Error adding location reference:', error)
      setIsGeneratingLocationReference(false)
    }
  }

  const handleBatchUpdateLocations = async (
    updatedLocationMap,
    modifiedKeys
  ) => {
    setIsGeneratingLocationReference(true)

    try {
      // Filter out URIs for modified keys so they get regenerated
      const filteredLocationUris = Object.keys(
        formData.locationReference || {}
      ).reduce((acc, key) => {
        if (!modifiedKeys.includes(key)) {
          acc[key] = formData.locationReference[key]
        }
        return acc
      }, {})

      const locationRes = await generateLocationReference({
        location_map_with_prompts: updatedLocationMap,
        existing_location_uris: filteredLocationUris
      })

      const taskId = locationRes?.data?.task_id

      if (taskId) {
        // reuse pollAddLocationTaskStatus as logic is same (update map & ref)
        await pollAddLocationTaskStatus(taskId, updatedLocationMap)
      } else {
        setIsGeneratingLocationReference(false)
      }
    } catch (error) {
      console.error('Error batch updating location references:', error)
      setIsGeneratingLocationReference(false)
    }
  }

  const pollAddLocationTaskStatus = async (taskId, newLocationMap) => {
    while (true) {
      try {
        const response = await getTaskStatus(taskId)

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.output
        ) {
          setFormData(prev => ({
            ...prev,
            locationReference: {
              ...(prev.locationReference || {}),
              ...response.data.output
            },
            locationMap: {
              ...(prev.locationMap || {}),
              ...newLocationMap
            }
          }))
          setIsGeneratingLocationReference(false)
          break
        } else if (response.data && response.data.status === 'failed') {
          console.error('Add location reference task failed:', response.data)
          setIsGeneratingLocationReference(false)
          break
        } else {
          setLocationGenerationProgress(
            response.data && response.data.progress
              ? response.data.progress
              : locationGenerationProgress
          )
          setLocationGenerationStatus(
            response.data && response.data.status ? response.data.status : ''
          )
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
        }
      } catch (error) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
      }
    }
  }

  const formatSceneObj = sceneObj => {
    return Object.entries(sceneObj).map(([sceneKey, sceneValue]) => {
      const { scene_location = '', shots = [] } = sceneValue

      // Format individual shots within the scene
      const formattedShots = shots.map((shotObj, index) => {
        // Extract the actual shot data - each item in shots array is an object with a single key like "shot 1"
        const shotKey = Object.keys(shotObj)[0]
        const shotValue = shotObj[shotKey]

        console.log('onsave shotValue', shotValue)

        return {
          id: `${sceneKey}-shot-${index + 1}`,
          key: shotKey,
          title: shotValue.title || '',
          initial_image_prompt:
            shotValue['initial image prompt'] ||
            shotValue.initial_image_prompt ||
            '',
          scenery: shotValue.scenery || '',
          shot_direction:
            shotValue['shot direction'] || shotValue.shot_direction || '',
          video_prompt:
            shotValue['video prompt'] || shotValue.video_prompt || '',
          voiceover_text:
            shotValue['voiceover text'] || shotValue.voiceover_text || '',
          lighting: shotValue.lighting || '',
          dialogue: shotValue.dialogue || {},
          characters: shotValue.characters || [], // Preserve characters array
          video_duration: shotValue.video_duration || ''
        }
      })

      return {
        id: sceneKey,
        title: sceneKey,
        description: scene_location,
        shots: formattedShots,
        aggregated_voiceover: sceneValue.aggregated_voiceover || '',
        audio_duration: sceneValue.audio_duration || 0,
        audio_duration_estimated: sceneValue.audio_duration_estimated || false,
        audio_link: sceneValue.audio_link || '',
        scene_location: scene_location
      }
    })
  }

  const updateStepValidation = (key, isValid) => {
    setStepsValidation(prev => {
      if (prev[key] === isValid) return prev
      console.log('updateStepValidation', key, isValid)
      return {
        ...prev,
        [key]: isValid
      }
    })
  }

  const renderStepContent = () => {
    const { key, component: StepComponent } = steps[currentStep]

    /* Steps Components */
    // VideoGeneration
    if (currentStep === steps.length - 1) {
      return (
        <StepComponent
          ref={videoGenerationRef}
          icon={steps[currentStep].icon}
          formData={formData}
          setFormData={setFormData}
          onValidationChange={isValid => updateStepValidation(key, isValid)}
          onProceed={handleNext}
          onGoBack={handlePrevious}
          isGeneratingLocationReference={isGeneratingLocationReference}
          onRegenerateShots={() => fetchShots()}
          isClusteringAudio={isClusteringAudio}
          videoGenerating={videoGenerating}
          generatingVideoForAllScenes={generatingVideoForAllScenes}
          handleRegenerateVideo={handleRegenerateVideo}
          handleRetryFailedVideos={handleRetryFailedVideos}
          handleGenerateFullVideo={generateAllVideos}
          updateShotDataRef={updateShotDataRef}
          videoProvider={videoProvider}
          setScenes={setScenes}
        />
      )
    }

    // ShotList
    if (currentStep === steps.length - 3) {
      return (
        <StepComponent
          ref={imageGenerationRef}
          icon={steps[currentStep].icon}
          formData={formData}
          setFormData={setFormData}
          onValidationChange={isValid => updateStepValidation(key, isValid)}
          onEditLocationReference={handleEditLocationReference}
          onProceed={handleNext}
          onGoBack={handlePrevious}
          onFineTunedLoading={() => setIsFineTunedLoading(prev => !prev)}
          isGeneratingLocationReference={isGeneratingLocationReference}
          shotsGenerationProgress={shotsGenerationProgress}
          locationGenerationProgress={locationGenerationProgress}
          onRegenerateShots={() => fetchShots()}
          onRegenerateLocationReference={() =>
            generateLocationMap(formData.locationMap)
          }
          onAddLocationReference={handleAddLocationReference}
          isClusteringAudio={isClusteringAudio}
          isFetchingCharacters={isFetchingCharacters}
          characters={characters}
          setCharacters={setCharacters}
          fetchCharacters={fetchCharacters}
          onBatchUpdateLocations={handleBatchUpdateLocations}
          // Pass navigation helper
          onNavigateToStep={stepKey => {
            const stepIndex = steps.findIndex(s => s.key === stepKey)
            if (stepIndex !== -1) {
              setCurrentStep(stepIndex)
            }
          }}
        />
      )
    }

    // VideoStyle step - pass style change handler
    if (currentStep === 2) {
      // videoStyle step index is 2
      return (
        <StepComponent
          icon={steps[currentStep].icon}
          formData={formData}
          setFormData={setFormData}
          onValidationChange={isValid => updateStepValidation(key, isValid)}
          onProceed={handleNext}
          onGoBack={handlePrevious}
          onStyleChange={handleVideoStyleChange}
        />
      )
    }

    // For other steps, render normally
    return StepComponent ? (
      <StepComponent
        icon={steps[currentStep].icon}
        formData={formData}
        setFormData={setFormData}
        onValidationChange={isValid => updateStepValidation(key, isValid)}
        onProceed={handleNext}
        isGeneratingLocationReference={isGeneratingLocationReference}
        onRegenerateShots={() => fetchShots()}
        onGoBack={handlePrevious}
        isClusteringAudio={isClusteringAudio}
        // New props from lifted state
        imageProvider={imageProvider}
        isImageGenerationLoading={isImageGenerationLoading}
        setIsImageGenerationLoading={setIsImageGenerationLoading}
        generateAllImages={generateAllImages}
        handleRegenerateImage={handleRegenerateImage}
        handleSelectReferenceAndRegenerate={handleSelectReferenceAndRegenerate}
        retryFailedImages={retryFailedImages}
        audioClusteringProgress={audioClusteringProgress}
        audioClusteringStatus={audioClusteringStatus}
        // Pass navigation to other components if needed, though mainly ImageGeneration needs it for VoiceoverModal
        onNavigateToStep={stepKey => {
          const stepIndex = steps.findIndex(s => s.key === stepKey)
          if (stepIndex !== -1) {
            setCurrentStep(stepIndex)
          }
        }}
        // Pass lifted voice state
        voices={voices}
        setVoices={setVoices}
        onVoiceChange={handleVoiceChange}
      />
    ) : null
  }

  const formatShotListForClustering = shotList => {
    const sceneStructure = {}

    shotList.forEach((scene, index) => {
      const sceneKey = `scene ${index + 1}`
      const shotsObj = {}

      scene.shots.forEach((shot, sIndex) => {
        const shotKey = `shot ${sIndex + 1}`
        shotsObj[shotKey] = {
          title: shot.title || '',
          shot_direction: shot.shot_direction || '',
          voiceover_text: shot.voiceover_text || '',
          initial_image_prompt: shot.initial_image_prompt || '',
          video_prompt: shot.video_prompt || '',
          scenery: shot.scenery || '',
          location: shot.location || '',
          lighting: shot.lighting || '',
          dialogue: shot.dialogue || {},
          // Reset duration related fields for clustering as request implies specific structure
          video_duration: null
        }
      })

      sceneStructure[sceneKey] = {
        scene_location: scene.scene_location || '',
        aggregated_voiceover: scene.aggregated_voiceover || '',
        audio_link: null,
        audio_duration: null,
        audio_duration_estimated: true,
        estimated_audio_duration: scene.estimated_audio_duration || null,
        shots: shotsObj
      }
    })

    return sceneStructure
  }

  const handleVoiceChange = voiceId => {
    const isGeneratingShots = formData.shotLoading || isFetchingShots.current
    const hasGeneratedMedia = (formData.shotList || []).some(
      scene =>
        scene.audio_link ||
        scene.audio_duration ||
        scene.shots.some(shot => shot.vidSrc || shot.video_duration)
    )

    // 1. Prepare updated shotList (clear duration-dependent assets ONLY if they exist)
    let updatedShotList = formData.shotList
    if (hasGeneratedMedia) {
      updatedShotList = (formData.shotList || []).map(scene => ({
        ...scene,
        // formattedShotListForClustering handles resetting these for the payload,
        // but we clean state here too for UI consistency
        audio_link: null,
        audio_duration: null,
        audio_duration_estimated: true,
        shots: scene.shots.map(shot => ({
          ...shot,
          vidSrc: null,
          video_duration: null,
          audio_duration: null,
          generated_video_duration: null
        }))
      }))
    }

    // 2. Update formData
    setFormData(prev => ({
      ...prev,
      selectedVoice: voiceId,
      voiceoverAudio: null,
      shotList: updatedShotList
    }))

    // 3. Trigger Audio Clustering with new voice
    // Only if we have content to regenerate and aren't currently generating shots
    if (hasGeneratedMedia && !isGeneratingShots) {
      const formattedData = formatShotListForClustering(updatedShotList)
      initiateAudioClustering(formattedData, null)
    }
  }

  const getPreviousNextStepTitles = () => {
    const previousStepIndex = Math.max(currentStep - 1, 0)
    const nextStepIndex = Math.min(currentStep + 1, steps.length - 1)

    return {
      previousStepTitle: steps[previousStepIndex].title,
      nextStepTitle: steps[nextStepIndex].title
    }
  }

  const shouldShowNextButton = () => {
    // Don't show Next button on the last step (VideoGeneration)
    if (currentStep === steps.length - 1) return false

    // For all other steps, show the Next button
    return true
  }

  const startMachine = async () => {
    await changeMachineState(1)
  }

  const stopMachine = async () => {
    await changeMachineState(0)
  }

  const isAllScenesGenerated = formData.shotList.every(scene =>
    scene.shots.every(shot => shot.vidSrc)
  )

  console.log({ formData, showProcessShots })

  return (
    <Spin spinning={isFetchingVideoData}>
      <div className="video-generation">
        <div className="video-generation__header">
          <div className="video-generation__header_top">
            <div className="video-generation__header_info">
              <div
                className="video-generation__header_info-icon"
                onClick={handleGoBack}
              >
                <Tooltip title="Back to Video List">
                  <Icon type="arrow-left" />
                </Tooltip>
              </div>

              <div className="video-generation__header_title">
                <div className="title-row">
                  <h1>Create Video</h1>
                  <div className="header-badge">
                    <Icon
                      type={steps[currentStep].icon}
                      className="badge-icon"
                    />
                    <span className="badge-text">
                      {steps[currentStep].title}
                    </span>
                    <span className="badge-divider" />
                    <span className="badge-count">
                      {currentStep + 1} of {steps.length}
                    </span>
                  </div>
                </div>
                <p className="header-subtitle-text">
                  Follow steps to generate AI video
                </p>
              </div>
            </div>
            <NewHeader />
          </div>

          <div className="video-generation__progress-container">
            <div
              className="video-generation__progress-bar"
              style={{
                width: `${((currentStep + 1) / steps.length) * 100}%`
              }}
            />
          </div>
        </div>
        <div className="video-generation__container">
          <div className="video-generation__content">{renderStepContent()}</div>
          <div className="video-generation__navigation">
            <div className="video-generation__navigation-left">
              {currentStep > 0 && (
                <Button
                  size="large"
                  onClick={handlePrevious}
                  className="nav-button"
                >
                  <Icon type="left" />
                  {getPreviousNextStepTitles().previousStepTitle}
                </Button>
              )}
            </div>
            <div className="video-generation__navigation-right">
              {currentStep > 0 && (
                <Button
                  size="large"
                  loading={isSavingVideoData}
                  onClick={handleSaveVideoData}
                >
                  {isSavingVideoData ? 'Saving...' : 'Save'}
                </Button>
              )}
              {shouldShowNextButton() && (
                <Button
                  type="primary"
                  size="large"
                  onClick={handleNext}
                  disabled={
                    currentStep === steps.length - 1 ||
                    !stepsValidation[steps[currentStep].key] ||
                    (steps[currentStep].key === 'shotList' &&
                      (isFetchingCharacters ||
                        isGeneratingLocationReference)) ||
                    (steps[currentStep].key === 'videoStyle' &&
                      !formData.videoStyle)
                  }
                  className="nav-button"
                >
                  Next: {getPreviousNextStepTitles().nextStepTitle}
                  <Icon type="right" />
                </Button>
              )}

              {currentStep === steps.length - 1 && (
                <Button
                  type="primary"
                  size="large"
                  shape="round"
                  icon="play-circle"
                  disabled={!isAllScenesGenerated}
                  onClick={handleOpenVideoEditor}
                >
                  Generate Full Video
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal
        visible={isCloning}
        footer={null}
        closable={false}
        centered
        width={300}
        bodyStyle={{ padding: '32px 24px', textAlign: 'center' }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Spin size="large" />
          <h3
            style={{
              marginTop: '16px',
              fontSize: '16px',
              fontWeight: 500,
              color: '#374151',
              marginBottom: '8px'
            }}
          >
            Cloning Video...
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: 0 }}>
            Please wait while we create a new copy.
          </p>
        </div>
      </Modal>
    </Spin>
  )
}

export default VideoAi
